import { Form, Formik } from 'formik';
import get from 'lodash/get';
import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useEffectOnce, useToggle } from 'react-use';
import { color_groups as COLOR_GROUPS } from '../../../js/app/react/colors';
import { PrimaryButton, SecondaryButton } from '../../CommonComponents/Button';
import ReactHighcharts from '../../CommonComponents/Highcharts';
import {
  currentClientSelector,
  isAdviserArea as isAdviserAreaSelector,
  selectIsHiddenLeversRTQ,
  selectShowPortfolioProjectedReturns,
  showRiskToleranceWorkflowSelector,
} from '../../CommonSelectors/CommonAreaSelector';
import { urlWithParams } from '../../CommonUtils/urlWithParams';
import showConfirmationDialog from '../../helpers/showConfirmationDialog';
import { RiskProfileSummary } from '../../RiskProfileSummary/RiskProfileSummary';
import useApi from '../../_hooks/useApi';
import LoadingView from './components/LoadingView';
import { setSelectedGroupModel } from './RiskToleranceActions';
import {
  ADVISER_AREA_SUBTITLE,
  ASSIGN_RISKALYZE_PORTFOLIO,
  CHART_OPTIONS,
  CONSUMER_AREA_SUBTITLE,
  FETCH_RISK_TOLERANCE,
} from './RiskToleranceConstants';
import {
  selectDisclosures,
  selectGroupOptions,
  selectGroupsById,
  selectIsRiskSourceOrion,
  selectModelsById,
  selectRiskalyseProfile,
  selectSelectedGroupId,
  selectSelectedModelId,
  selectShowRiskalysePopup,
} from './RiskToleranceSelector';
import {
  ChartArea,
  ChartContainer,
  ChartSection,
  Container,
  Detail,
  Disclosure,
  Footer,
  LabelText,
  LeftWrapper,
  ModelGroupSelect,
  Models,
  PortfolioItem,
  Portfolios,
  RiskBand,
  RiskinessImage,
  WorkflowWrapper,
  Wrapper,
} from './RiskToleranceStyles';
import ConfirmationModal from '../../CommonComponents/ConfirmationModal/ConfirmationModalView';
import { ProfilePageHeader } from '../../ProfileOPL/components/ProfilePageHeader/ProfilePageHeader';
import { WorkflowFooter } from '../../Workflow/components/WorkflowFooter/WorkflowFooter';
import { WorkflowComponentProps } from '../../Workflow/Workflow.meta';
import { WorkflowIdEnum } from '../../Workflow/api/useWorkflowDataQuery';
import { HiddenLeversRiskScore } from '../Components/workflows/HiddenLeversRTQ/components/HiddenLeversRiskScore/HiddenLeversRiskScore';
import { Card } from '../../CommonComponents/OPL/Card/Card';
import { OrionResult } from './components/OrionResult/OrionResult';
import { Box } from '../../CommonComponents/OPL/Box/Box';

enum ExternalSystemId {
  Orion = 2,
  Riskalyze = 5,
}

interface Props {
  clientId: string;
  workflowProps?: WorkflowComponentProps;
}

const RiskTolerance: React.FC<Props> = ({ clientId, workflowProps }) => {
  const dispatch = useDispatch();

  const isAdviserArea = useSelector(isAdviserAreaSelector);
  const showRiskToleranceWorkflow = useSelector(showRiskToleranceWorkflowSelector);
  const groupOptions = useSelector(selectGroupOptions);
  const groupsById = useSelector(selectGroupsById);
  const modelsById = useSelector(selectModelsById);
  const disclosure = useSelector(selectDisclosures);
  const selectedGroupId = useSelector(selectSelectedGroupId);
  const selectedModelId =
    useSelector(selectSelectedModelId) ||
    get(groupsById, `${selectedGroupId}.defaultPortfolioId`, null);
  const riskalyseProfile = useSelector(selectRiskalyseProfile);
  const currentClient = useSelector(currentClientSelector);
  const showRiskalysePopup = useSelector(selectShowRiskalysePopup);
  const isRiskSourceFromOrion = useSelector(selectIsRiskSourceOrion);
  const isHiddenLeversRTQ = useSelector(selectIsHiddenLeversRTQ);
  const showPortfolioProjectedReturns = useSelector(selectShowPortfolioProjectedReturns);

  const [dataChanged, toggleDataChanged] = useToggle(false);

  const [fetchRiskTolerance, { loading: loadingRiskTolerance }] = useApi(
    urlWithParams('/Consumer/RiskToleranceStep2', {
      clientId,
      withRiskalyse: currentClient.HasOrionRiskAlyseIntegration,
    }),
    {
      reduxActionPrefix: FETCH_RISK_TOLERANCE,
    },
  );

  const [saveRiskTolerance] = useApi('/Consumer/RiskToleranceUpdate2/Save', {
    method: 'POST',
    bodyEncoding: 'formData',
  });

  const [assignRiskalyzePortfolio, { loading: loadingAssignRiskalyzePortfolio }] = useApi(
    urlWithParams('/Consumer/RiskToleranceUpdate2/PromptAssignRiskalysePortfolio', {
      clientId,
      assignRiskalysePortfolio: 'true',
    }),
    { method: 'POST', reduxActionPrefix: ASSIGN_RISKALYZE_PORTFOLIO },
  );

  const [dontAssignRiskalyzePortfolio, { loading: loadingDontAssignRiskalyzePortfolio }] = useApi(
    urlWithParams('/Consumer/RiskToleranceUpdate2/PromptAssignRiskalysePortfolio', {
      clientId,
      assignRiskalysePortfolio: 'false',
    }),
    { method: 'POST', reduxActionPrefix: ASSIGN_RISKALYZE_PORTFOLIO },
  );

  useEffectOnce(() => {
    fetchRiskTolerance();

    const helpKey = ADV.util.setHelp(ADV.HelpDescriptors.RiskToleranceStep);

    return () => {
      ADV.util.unsetHelp(helpKey);
    };
  });

  useEffect(() => {
    if (dataChanged) {
      ADV.util.Router.registerNavigateAwayCallback(showConfirmationDialog);
    } else {
      ADV.util.Router.unregisterNavigateAwayCallback();
    }
  }, [dataChanged]);

  const initialValues = {
    selectedModelId,
    selectedGroupId,
  };

  return (
    <Formik
      enableReinitialize
      initialValues={initialValues}
      onSubmit={(values, { setSubmitting, resetForm }) => {
        if (!dataChanged) {
          setSubmitting(false);

          if (workflowProps) {
            workflowProps.onNext();
          }

          return;
        }

        saveRiskTolerance({
          body: {
            clientId,
            selectedPortfolioId: values.selectedModelId,
            selectedGroupId: values.selectedGroupId,
          },
          onSuccess() {
            dispatch(setSelectedGroupModel(values.selectedGroupId, values.selectedModelId));

            resetForm({
              selectedModelId: values.selectedModelId,
              selectedGroupId: values.selectedGroupId,
            });

            if (workflowProps) {
              workflowProps.onNext();
            }
          },
          onFinish() {
            setSubmitting(false);
          },
        });
      }}
      render={({ values, setFieldValue, resetForm, isSubmitting, dirty }) => {
        toggleDataChanged(dirty);

        const TopWrapper = workflowProps ? WorkflowWrapper : React.Fragment;

        const onAssessRiskToleranceClick = () => {
          ADV.util.Router.go2('RiskToleranceWorkflow', {
            clientId,
            redirectTo: !workflowProps
              ? 'profile'
              : workflowProps.workflowId === WorkflowIdEnum.AssetBucketing
              ? 'assetBucketing'
              : 'comprehensive',
          });
        };

        return (
          <Form>
            <Container id="Profile_RiskTolerance" className="Profile">
              <TopWrapper>
                <ProfilePageHeader
                  title={isHiddenLeversRTQ ? 'Risk Profile' : 'Risk Tolerance'}
                  description={isAdviserArea ? ADVISER_AREA_SUBTITLE : CONSUMER_AREA_SUBTITLE}
                />

                {loadingRiskTolerance ? (
                  <LoadingView
                    hasOrionRiskAlyseIntegration={currentClient.HasOrionRiskAlyseIntegration}
                  />
                ) : (
                  <>
                    {riskalyseProfile && (
                      <RiskProfileSummary {...riskalyseProfile} style={{ marginBottom: 48 }} />
                    )}

                    {(currentClient.HasOrionRiskAlyseIntegration
                      ? !riskalyseProfile?.recommendedPortfolio
                      : showRiskToleranceWorkflow) && (
                      <Box mb="32">
                        {isRiskSourceFromOrion ? (
                          <OrionResult onRetakeAssessment={onAssessRiskToleranceClick} />
                        ) : (
                          <HiddenLeversRiskScore onRetakeAssessment={onAssessRiskToleranceClick} />
                        )}
                      </Box>
                    )}

                    <Card p="32">
                      <Wrapper>
                        <LeftWrapper>
                          {isAdviserArea && (
                            <label htmlFor="selectedGroupId">
                              <LabelText>Select a portfolio group</LabelText>
                              <ModelGroupSelect
                                name="selectedGroupId"
                                isClearable={false}
                                getOptionLabel={({ groupName }) => groupName}
                                getOptionValue={({ groupId }) => groupId}
                                options={groupOptions}
                                value={groupsById[values.selectedGroupId]}
                                onChange={({ groupId }) => {
                                  setFieldValue('selectedGroupId', groupId);
                                  setFieldValue(
                                    'selectedModelId',
                                    get(groupsById, `${groupId}.defaultPortfolioId`, null),
                                  );
                                }}
                              />
                            </label>
                          )}

                          <LabelText>
                            Select a model portfolio based on your{' '}
                            {isHiddenLeversRTQ ? 'risk profile' : 'risk tolerance'}
                          </LabelText>
                          <Portfolios>
                            {get(groupsById, `${values.selectedGroupId}.modelPortfolios`, []).map(
                              model => (
                                <PortfolioItem
                                  key={model.id}
                                  isSelected={values.selectedModelId === model.id}
                                  onClick={() => setFieldValue('selectedModelId', model.id)}
                                >
                                  {groupsById[values.selectedGroupId].externalSystem ===
                                  ExternalSystemId.Riskalyze ? (
                                    <RiskinessImage
                                      alt="Riskalyze Risk"
                                      src={`https://1dc018be4716da4aaa18-5d73971c6ce0cd6d739efc4134c4b48a.ssl.cf2.rackcdn.com/v2/standard/60/r${model.riskScore}.png`}
                                    />
                                  ) : (
                                    showRiskToleranceWorkflow && (
                                      <RiskBand>{`${model.riskRange.from}-${model.riskRange.to}`}</RiskBand>
                                    )
                                  )}

                                  <div>{model.portfolioName}</div>
                                </PortfolioItem>
                              ),
                            )}
                          </Portfolios>
                        </LeftWrapper>

                        <Models>
                          <ChartArea isEmpty={!values.selectedModelId}>
                            {modelsById[values.selectedModelId] && (
                              <>
                                <ChartSection sameLine>
                                  <LabelText>Portfolio Name</LabelText>
                                  <Detail>
                                    {modelsById[values.selectedModelId].portfolioName}
                                  </Detail>
                                </ChartSection>
                                <ChartSection>
                                  <LabelText>Asset Allocation</LabelText>
                                  <ChartContainer>
                                    <ReactHighcharts
                                      options={{
                                        ...CHART_OPTIONS,
                                        colors: modelsById[
                                          values.selectedModelId
                                        ].assetAllocation.map(
                                          ({ color, color_group: colorGroup }) =>
                                            color || COLOR_GROUPS[colorGroup][0],
                                        ),
                                        series: [
                                          {
                                            // eslint-disable-next-line @typescript-eslint/ban-ts-ignore
                                            // @ts-ignore
                                            ...CHART_OPTIONS.series[0],
                                            data: modelsById[
                                              values.selectedModelId
                                            ].assetAllocation.map(({ name, y }) => ({ name, y })),
                                          },
                                        ],
                                      }}
                                    />
                                  </ChartContainer>
                                </ChartSection>
                                {modelsById[values.selectedModelId].portfolioDescription && (
                                  <ChartSection>
                                    <LabelText>Description</LabelText>
                                    <Detail>
                                      {modelsById[values.selectedModelId].portfolioDescription}
                                    </Detail>
                                  </ChartSection>
                                )}

                                {showPortfolioProjectedReturns && (
                                  <ChartSection>
                                    <LabelText>Projected Return</LabelText>
                                    <Detail>
                                      {modelsById[values.selectedModelId].expectedReturn}
                                    </Detail>
                                  </ChartSection>
                                )}
                              </>
                            )}
                          </ChartArea>
                        </Models>
                      </Wrapper>
                      {!!disclosure && <Disclosure>{disclosure}</Disclosure>}
                    </Card>

                    {!workflowProps && (
                      <Footer>
                        <SecondaryButton onClick={() => resetForm(initialValues)}>
                          Reset
                        </SecondaryButton>
                        <PrimaryButton
                          type="submit"
                          isLoading={isSubmitting}
                          disabled={isSubmitting}
                        >
                          Save Changes
                        </PrimaryButton>
                      </Footer>
                    )}
                  </>
                )}
              </TopWrapper>
            </Container>

            {workflowProps && (
              <WorkflowFooter
                isSubmit
                isFirstStep={workflowProps.isFirstStep}
                isOnNextLoading={isSubmitting}
                isOnNextDisabled={values.selectedModelId === null}
                onPrevious={workflowProps.onPrevious}
              />
            )}

            <ConfirmationModal
              isOpen={showRiskalysePopup}
              title="We noticed a model assigned to the above risk profile."
              message="Would you like to assign this same model to the client for financial planning outcome purposes?"
              actions={
                <>
                  <SecondaryButton
                    onClick={dontAssignRiskalyzePortfolio}
                    disabled={
                      loadingDontAssignRiskalyzePortfolio || loadingAssignRiskalyzePortfolio
                    }
                    isLoading={loadingDontAssignRiskalyzePortfolio}
                  >
                    No
                  </SecondaryButton>
                  <PrimaryButton
                    onClick={assignRiskalyzePortfolio}
                    disabled={
                      loadingAssignRiskalyzePortfolio || loadingDontAssignRiskalyzePortfolio
                    }
                    isLoading={loadingAssignRiskalyzePortfolio}
                  >
                    Yes
                  </PrimaryButton>
                </>
              }
            />
          </Form>
        );
      }}
    />
  );
};

export default RiskTolerance;
